import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useAppConfig } from "../../../AppConfigProvider";
import { useFocusEffect, useRoute, RouteProp } from "@react-navigation/native";
import { StoragePathType, useProcessAttachmentMutation, useGetFileQuery } from "../../../graphql/generated/graphql";
import ChatBar from "../../../components/navigation/ChatBar/ChatBar";
import UpgradeModalContainer from "../../general/UpgradeModalContainer/UpgradeModalContainer";
import useAudioRecorder from "../../../hooks/useAudioRecorder";
import { useChatBar } from "../../../contexts/ChatBarContext";
import { usePostHog } from "posthog-js/react";
import useWebSocket from "../../../hooks/useWebSocket";
import { useChatBarInput } from "../../../contexts/CharBarInputContext";
import Sentry from "../../../utils/sentry";
import { View, Text, StyleSheet } from "react-native";
import useFileUpload, { ProcessedFile } from "../../../hooks/useFileUpload";
import { useAuth } from "../../../hooks/useAuth";
import { getValidAccessToken } from "../../../utils/authRefresh";
import type { RootStackParamList } from "../../../navigation/navigation.types";

const viewsWithoutAttachments = ["Onboarding", "Custom Resource Student"];

function ChatBarContainer({ onSubmit = () => {} }) {
  const { companionName, isAxioOffering } = useAppConfig();
  const posthog = usePostHog();

  const { state, handleAudioOffPress, handleAudioOnPress, dispatch } = useAppState();
  const { setShowChat, showChat, state: chatBarState, setMetaIsLoading, setSubmitEnabled } = useChatBar();
  const { setInputValue, inputValue } = useChatBarInput();
  const { sendMessage } = useWebSocket();

  const route = useRoute<RouteProp<RootStackParamList, keyof RootStackParamList>>();

  const { meData } = useAuth();
  const {
    data: fileData,
    startPolling,
    stopPolling,
    refetch,
  } = useGetFileQuery({ variables: { fileId: "" }, fetchPolicy: "network-only" });

  const chatInputRef = useRef(null);

  const { recording, toggleRecording } = useAudioRecorder(onSubmit);

  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [statusAnnouncement, setStatusAnnouncement] = useState("");

  const [processAttachment] = useProcessAttachmentMutation();

  const isAttachmentEnabled = !viewsWithoutAttachments.includes(route.name);

  useFocusEffect(
    useCallback(() => {
      // Function to execute when the screen is focused
      const handleDocumentKeyDown = (e: KeyboardEvent) => {
        // Only focus chat input on printable character keys
        if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;

        const activeEl = document.activeElement;
        const isInputActive =
          activeEl instanceof HTMLInputElement ||
          activeEl instanceof HTMLTextAreaElement ||
          activeEl?.getAttribute("contenteditable") === "true";
        if (isInputActive) return;
        chatInputRef.current?.focus();
      };

      if (state.meta.keypressIsListening) {
        // Add the event listener only if input is not focused and the modal is not open
        document.addEventListener("keydown", handleDocumentKeyDown);
      } else {
        // Remove the event listener if input is focused or the modal is open
        document.removeEventListener("keydown", handleDocumentKeyDown);
      }

      // Cleanup the event listener when the component is unmounted
      return () => {
        document.removeEventListener("keydown", handleDocumentKeyDown);
      };
    }, [state.meta.keypressIsListening])
  );

  useEffect(() => {
    if (fileData?.getFile?.data?.length > 0) {
      const file = fileData.getFile.data[0];
      const isLoading = file.status !== "completed";
      if (!isLoading) {
        setIsAttachmentLoading(false);
        stopPolling();
        setSubmitEnabled(true);
      }
    }
  }, [fileData, setIsAttachmentLoading, stopPolling]);

  const handleRecordingStopPress = () => {
    toggleRecording();
    setStatusAnnouncement("Recording stopped");
  };

  const handleRecordingStartPress = () => {
    if (!meData?.me?.isPro) {
      setIsUpgradeModalVisible(true);
    } else {
      toggleRecording();
      setStatusAnnouncement("Recording started");
    }
  };

  const handleCloseUpgradeModals = () => {
    setIsUpgradeModalVisible(false);
  };

  const handleToggleChat = () => {
    setShowChat(!showChat);
  };

  const handleQuickOptionPress = (value) => {
    handleSubmitEditing(value);
  };

  const handleSubmit = () => {
    handleSubmitEditing(undefined);
    onSubmit();
  };

  const handleChangeText = (text) => {
    setInputValue(text);
  };

  const handleBeforeUpload = () => {
    setIsAttachmentLoading(true);
    setSubmitEnabled(false);
  };

  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    for (const processedFile of processedFilesArray) {
      await processAttachment({
        variables: {
          fileId: processedFile.file.id,
        },
      });
      // Add the new file to the files array
      setAttachment({
        title: processedFile.file.fileName,
        fileType: processedFile.mimeType,
        id: processedFile.file.id,
        status: "pending",
      });
      refetch({ fileId: processedFile.file.id });
    }

    startPolling(3000);
    setStatusAnnouncement("File uploaded successfully");
  };

  const handleFileUploadError = () => {
    setIsAttachmentLoading(false);
    setSubmitEnabled(true);
    setAttachment(null);
    setStatusAnnouncement("File upload failed. Please try again.");
  };

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: handleBeforeUpload,
    afterUpload: handleAfterUpload,
    onError: handleFileUploadError,
    multiple: true,
  });

  const handleDeleteAttachment = () => {
    setAttachment(null);
  };

  const handleSubmitEditing = async (quickItemValue) => {
    try {
      if (isAttachmentLoading === true) return;

      posthog?.capture("Message sent to axio");
      setAttachment(null);

      if (state.meta.isAdventure) {
        dispatch({
          type: actions.SET_META,
          payload: { isAdventure: false },
        });
      }

      await sendMessageToAxio(quickItemValue);

      setShowChat(true);

      setInputValue("");
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const sendMessageToAxio = async (quickItemValue) => {
    const token = await getValidAccessToken();

    setMetaIsLoading(true);
    sendMessage({
      ...chatBarState.llmMessage,
      meta: {
        ...chatBarState.llmMessage.meta,
        token,
        isLoading: true,
        attachmentId: attachment ? attachment.id : false,
      },
      content: { ...chatBarState.llmMessage.content, message: quickItemValue || inputValue },
      state: {
        ...chatBarState.llmMessage.state,
      },
    });
  };

  const onAudioOffPress = async () => {
    await handleAudioOffPress();
  };

  const onAudioOnPress = async () => {
    if (!meData?.me?.isPro && meData?.me?.isOnboarded) {
      setIsUpgradeModalVisible(true);
    } else {
      await handleAudioOnPress();
    }
  };

  const placeholderText = recording ? `${companionName} is listening...` : `Chat with ${companionName}...`;

  return (
    <View
      style={{
        width: "100%",
        marginHorizontal: "auto",
        display: "flex",
        justifyContent: "center",
        flexDirection: "row",
      }}>
      <View aria-live="polite" aria-atomic="true" accessibilityLiveRegion="polite" style={styles.srOnly}>
        <Text>{statusAnnouncement}</Text>
      </View>
      <ChatBar
        value={inputValue}
        isRecording={recording}
        isAudioOn={state.meta.isAudioOn}
        placeholder={placeholderText}
        inputRef={chatInputRef}
        onChangeText={handleChangeText}
        onSubmitEditing={handleSubmit}
        onAudioOffPress={onAudioOffPress}
        onAudioOnPress={onAudioOnPress}
        onRecordingStopPress={handleRecordingStopPress}
        onRecordingStartPress={handleRecordingStartPress}
        onToggleChatPress={handleToggleChat}
        onQuickOptionPress={handleQuickOptionPress}
        onAttachmentPress={handleFileUpload}
        onDeleteAttachmentPress={handleDeleteAttachment}
        isCustomOffering={isAxioOffering}
        showQuickOptions={true}
        showChat={showChat}
        isSubmitEnabled={chatBarState.isSubmitEnabled}
        isAttachmentEnabled={isAttachmentEnabled}
        attachment={attachment}
        isAttachmentLoading={isAttachmentLoading}
        chatWindowDisabled={false}
        inputAccessibilityLabel="Chat with companion"
      />

      <UpgradeModalContainer visible={isUpgradeModalVisible} onClose={handleCloseUpgradeModals} />
    </View>
  );
}

const styles = StyleSheet.create({
  srOnly: {
    position: "absolute",
    left: -10000,
    top: -10000,
    width: 1,
    height: 1,
    overflow: "hidden",
  },
});

export default ChatBarContainer;
